/**
 * Header: fixed, transparent over the hero, opaque elsewhere.
 * The colour swap uses the shared ScrollTrigger when that engine is already
 * active. The current video-only homepage has no engine consumer, so a small
 * passive scroll/rAF fallback covers only that marker and reverses cleanly.
 */
/**
 * Resolve the shared motion singleton.
 *
 * Do NOT `import` sc-motion-core.js from here. Section scripts are served from
 * the same CDN directory, so a relative specifier resolves to the
 * UNVERSIONED asset URL while the layout loads the VERSIONED one. Two URLs
 * means two module instances — two Lenis controllers, two cleanup sets, and
 * a race over window.SCMotion.
 */
const Motion = new Proxy({}, { get: (_, k) => window.SCMotion?.[k] });

let trigger = null;
let spacer = null;
let videoFallbackOff = null;
let videoFallbackSchedule = null;
let headerGeneration = 0;

function stopVideoHeroFallback() {
  const off = videoFallbackOff;
  videoFallbackOff = null;
  videoFallbackSchedule = null;
  if (typeof off === 'function') off();
  document.documentElement.classList.remove('sc-video-header--over');
}

function mountVideoHeroFallback(header, hero) {
  let frame = null;
  let stopped = false;

  const update = () => {
    frame = null;
    if (stopped) return;
    if (!header.isConnected || !hero.isConnected) {
      stopVideoHeroFallback();
      document.documentElement.classList.remove('sc-video-header--over');
      return;
    }

    const headerRect = header.getBoundingClientRect();
    const heroRect = hero.getBoundingClientRect();
    document.documentElement.classList.toggle(
      'sc-video-header--over',
      heroRect.top <= headerRect.bottom && heroRect.bottom > headerRect.bottom,
    );
  };

  const schedule = () => {
    if (stopped || frame !== null) return;
    frame = requestAnimationFrame(update);
  };

  addEventListener('scroll', schedule, { passive: true });
  videoFallbackSchedule = schedule;
  update();
  schedule();

  return () => {
    stopped = true;
    removeEventListener('scroll', schedule);
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    if (videoFallbackSchedule === schedule) videoFallbackSchedule = null;
    document.documentElement.classList.remove('sc-video-header--over');
  };
}

function measure(header) {
  const h = Math.round(header.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--sc-header-h', `${h}px`);
  return h;
}

export function initHeader() {
  // Two elements carry the group class — the announcement bar and the
  // header itself. Only the <header> is the sticky one.
  const header = document.querySelector('header.shopify-section-group-header-group');
  if (!header) return () => {};
  const generation = headerGeneration;

  // Horizon's header is already sticky; no spacer and no position change
  // are needed, which also means no layout jump to guard against.
  measure(header);

  const onResize = () => {
    measure(header);
    videoFallbackSchedule?.();
  };
  addEventListener('resize', onResize, { passive: true });

  // Repoint Horizon's search control at the full-screen overlay, and add a
  // Menu trigger beside the navigation. Done at runtime because these are
  // static blocks in Horizon's header.
  const search = header.querySelector('[aria-label*="earch" i], a[href*="/search"]');
  if (search) {
    search.dataset.scOpen ||= 'search';
    // Horizon's declarative handler otherwise opens its native dialog before
    // the delegated custom-overlay handler sees the same click.
    search.removeAttribute('on:click');
  }

  const nav = header.querySelector('nav ul, .header-menu ul');
  // Guard on the menu trigger specifically. The search control also carries
  // data-sc-open and lives inside this nav, so a generic check always matched.
  if (nav && !nav.querySelector('[data-sc-open="menu"]')) {
    const make = (name, label) => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.dataset.scOpen = name;
      b.textContent = label;
      b.className = 'sc-nav-trigger';
      li.append(b);
      return li;
    };
    // Left group per the brief: Menu, Shop All, Categories.
    nav.prepend(make('categories', 'Categories'));
    const shopAll = document.createElement('li');
    shopAll.innerHTML = '<a href="/collections/all">Shop all</a>';
    nav.prepend(shopAll);
    nav.prepend(make('menu', 'Menu'));
  }

  return () => {
    if (generation !== headerGeneration) return;
    stopVideoHeroFallback();
    trigger?.kill();
    trigger = null;
    removeEventListener('resize', onResize);
    document.documentElement.classList.remove('sc-header--over');
  };
}

function mountHeaderMotion() {
  trigger?.kill();
  trigger = null;

  const header = document.querySelector('header.shopify-section-group-header-group');
  const visibleHeader = document.querySelector('#header-component');
  const legacyHero = document.querySelector('[data-sc-hero]');
  const videoHero = document.querySelector('[data-scr-video-hero]');
  const hero = legacyHero || videoHero;
  if (!header || !visibleHeader || !hero) {
    stopVideoHeroFallback();
    document.documentElement.classList.remove('sc-header--over');
    return;
  }

  // The current video homepage owns this small native driver even if an
  // unrelated section later starts the optional motion engine. This avoids a
  // mid-session geometry-owner handoff while preserving the legacy GSAP path.
  if (!legacyHero && videoHero) {
    document.documentElement.classList.remove('sc-header--over');
    if (!videoFallbackOff) videoFallbackOff = mountVideoHeroFallback(visibleHeader, videoHero);
    return;
  }

  stopVideoHeroFallback();
  if (!Motion.ScrollTrigger) {
    document.documentElement.classList.remove('sc-header--over');
    return;
  }

  trigger = Motion.ScrollTrigger.create({
    trigger: hero,
    start: 'top top',
    end: 'bottom top+=' + measure(header),
    onToggle: (self) =>
      document.documentElement.classList.toggle('sc-header--over', self.isActive),
  });
  document.documentElement.classList.add('sc-header--over');
}

// Ordinary header measurement and navigation wiring must not wait for the
// optional CDN engine. Only the hero colour trigger mounts on motion readiness.
// boot() tears the previous instance down first so editor section reloads stay single.
let headerOff = null;
let headerBooted = false;
const teardownHeader = () => {
  if (typeof headerOff === 'function') { try { headerOff(); } catch { /* already torn down */ } }
  headerOff = null;
  headerBooted = false;
  headerGeneration += 1;
};
const boot = () => {
  teardownHeader();
  headerOff = initHeader() || null;
  headerBooted = true;
  mountHeaderMotion();
  const videoOnly = !document.querySelector('[data-sc-hero]') && document.querySelector('[data-scr-video-hero]');
  if (headerOff && !videoOnly) Motion.onCleanup?.(headerOff);
};
document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target;
  if (
    section?.matches?.('[data-scr-video-hero]') ||
    section?.querySelector?.('[data-scr-video-hero]')
  ) {
    stopVideoHeroFallback();
    document.documentElement.classList.remove('sc-video-header--over');
  }
  if (
    section?.matches?.('.header-section, .shopify-section-group-header-group') ||
    section?.querySelector?.('header.shopify-section-group-header-group, #header-component')
  ) teardownHeader();
});
document.addEventListener('sc:motion:ready', () => {
  if (headerBooted) mountHeaderMotion();
});
document.addEventListener('shopify:section:load', boot);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

/* --- phone logo centring --------------------------------------------
 * Horizon's header grid places the logo off-centre on phones, and the
 * offset shifts with track content between loads — no static CSS value
 * holds. So: measure the real offset on the real device and cancel it
 * exactly. Desktop uses the absolute-centring CSS instead. */
const centreLogo = () => {
  const logo = document.querySelector('a.header-logo');
  const html = document.documentElement;
  if (!logo) return;
  if (innerWidth >= 750) { html.style.removeProperty('--sc-logo-shift'); return; }
  /* The shift lives in a CSS variable on <html> and is applied by a
     stylesheet rule — Horizon can re-render or rewrite the logo's own
     style attribute all it likes, the correction survives. */
  html.style.setProperty('--sc-logo-shift', '0px');
  const r = logo.getBoundingClientRect();
  if (!r.width) return;
  const off = r.left + r.width / 2 - innerWidth / 2;
  html.style.setProperty('--sc-logo-shift', `${-off}px`);
};
/* Run directly AND after a frame: rAF alone never fires in hidden or
   heavily throttled tabs, which parked the whole fix. */
const centreSoon = () => { centreLogo(); requestAnimationFrame(centreLogo); };
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', centreSoon, { once: true });
} else { centreSoon(); }
addEventListener('load', centreSoon, { once: true });
document.fonts?.ready.then(centreSoon);
/* The header lays out late (fonts, grid tracks, section JS) — early
   passes can measure a zero-width logo and bail. Sweep a few times;
   each pass is two style writes, invisible in cost. */
[600, 1500, 3000, 5000, 7500].forEach((ms) => setTimeout(centreSoon, ms));
/* Horizon re-renders the header late and re-assigns the logo's style
   attribute, wiping the correction. Watch for that and re-apply —
   bounded, so a style war can never loop forever. */
let scLogoReapplies = 0;
const scLogoWatch = () => {
  const logo = document.querySelector('a.header-logo');
  if (!logo) return;
  new MutationObserver(() => {
    if (innerWidth >= 750 || scLogoReapplies > 12) return;
    if (!(logo.style.transform || '').includes('translateX')) {
      scLogoReapplies += 1;
      centreSoon();
    }
  }).observe(logo, { attributes: true, attributeFilter: ['style'] });
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scLogoWatch, { once: true });
} else { scLogoWatch(); }
let scLogoW = innerWidth;
addEventListener('resize', () => {
  if (innerWidth === scLogoW) return;
  scLogoW = innerWidth;
  centreSoon();
}, { passive: true });
